import React, { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import NestedItemSelect, {
  BlueprintOption,
  BlueprintSection,
} from "@components/NestedItemSelect/NestedItemSelect";
import "./style.css";
import useGetProject from "./api/useGetProject";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Project } from "./types/Project";
import { TextField } from "@components/FormsV2";
import { useGetAccountQuery, useV2CreateBlueprintMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";

const col = (id: string, header: string): ColumnDef<BlueprintOption> => ({
  id,
  header,
  cell: (props) => (props.row.original[id] as string) || "",
});

const GENERAL_OPTIONS: BlueprintOption[] = [
  { id: "executive-summary", name: "Executive Summary", selected: true },
  { id: "solution-summary", name: "Solution Summary", selected: true },
  { id: "rate-table", name: "Rate Table", selected: true },
  { id: "payment-term", name: "Payment Term", selected: true },
];

const buildSections = (project: Project | null): BlueprintSection[] => [
  {
    id: "general",
    name: "General",
    selected: true,
    options: GENERAL_OPTIONS,
    columns: [col("name", "General Sections to Include")],
  },
  {
    id: "professional-services",
    name: "Professional Services",
    selected: true,
    options: (project?.services?.filter(
      (s) => s.serviceType == "professional_services"
    ) ?? []) as BlueprintOption[],
    columns: [
      col("name", "Professional Services To Include"),
      col("quantity", "QTY"),
      col("hours", "Unit Hrs"),
      col("resource", "Resource"),
    ],
  },
  {
    id: "managed-services",
    name: "Managed Services",
    selected: true,
    options: (project?.services?.filter(
      (s) => s.serviceType == "managed_services"
    ) ?? []) as BlueprintOption[],
    columns: [
      col("name", "Managed Services To Include"),
      col("quantity", "QTY"),
      col("price", "Monthly Price"),
      col("cost", "Monthly Cost"),
    ],
  },
  {
    id: "expense",
    name: "Expense",
    selected: true,
    options: (project?.expenses ?? []) as BlueprintOption[],
    columns: [
      col("name", "Expenses To Include"),
      col("quantity", "Quantity"),
      col("cost", "Cost"),
    ],
  },
  {
    id: "governance",
    name: "Governance",
    selected: true,
    options: (project?.governances ?? []) as BlueprintOption[],
    columns: [
      col("name", "Governances To Include"),
      col("phase", "Phase"),
      col("resource", "Resource"),
      col("calculation", "Calc Hrs By"),
      col("hours", "Hours"),
    ],
  },
  // {
  //   id: "vendor-quotes",
  //   name: "Vendor Quotes",
  //   selected: true,
  //   options: (project?.quotes ?? []) as BlueprintOption[],
  //   columns: [
  //     col("name", "Vendor Quotes To Include"),
  //     col("cost", "One Time Cost"),
  //     col("recCost", "Recurring Cost"),
  //     col("revenue", "One Time Revenue"),
  //     col("recRevenue", "Recurring Revenue"),
  //   ],
  // },
  {
    id: "product",
    name: "Product",
    selected: true,
    options: (project?.products ?? []) as BlueprintOption[],
    columns: [
      col("name", "Products To Include"),
      col("mfrPart", "MFR Part"),
      col("quantity", "Quantity"),
      col("units", "Units"),
      col("price", "Unit Price"),
    ],
  },
  {
    id: "resources",
    name: "Resources",
    selected: true,
    options: (project?.resources ?? []) as BlueprintOption[],
    columns: [
      col("name", "Resources To Include"),
      col("lob", "LOB/Category"),
      col("hourlyRate", "Hourly Rate"),
      col("expenseRate", "Travel/Expense Rate"),
    ],
  },
];

const buildPayload = (
  sections: BlueprintSection[],
  project: Project | null | undefined
): any[] => {
  const payload: any[] = [];

  const generalSection = sections.find((s) => s.id === "general");
  if (generalSection?.selected) {
    const selectedOptions =
      generalSection.options?.filter((o) => o.selected) ?? [];

    const attributes: Record<string, string> = {};
    const relationships: Record<string, any> = {};

    for (const option of selectedOptions) {
      if (option.id === "executive-summary") {
        attributes["executive-summary"] = project?.executiveSummary ?? "";
      } else if (option.id === "solution-summary") {
        attributes["solution-summary"] = project?.solutionSummary ?? "";
      } else if (option.id === "rate-table" && project?.rateTableId != null) {
        relationships["rate-table"] = {
          data: { id: Number(project.rateTableId), type: "rate-tables" },
        };
      } else if (
        option.id === "payment-term" &&
        project?.paymentTermId != null
      ) {
        relationships["payment-term"] = {
          data: { id: Number(project.paymentTermId), type: "payment-terms" },
        };
      }
    }

    const projectResource: any = { type: "projects", id: Number(project?.id) };
    if (Object.keys(attributes).length > 0)
      projectResource.attributes = attributes;
    if (Object.keys(relationships).length > 0)
      projectResource.relationships = relationships;

    payload.push(projectResource);
  }

  const psSection = sections.find((s) => s.id === "professional-services");
  if (psSection?.selected) {
    (psSection.options ?? [])
      .filter((s) => s.selected)
      .forEach((s) => payload.push((s as any).rawResource));
  }

  const msSection = sections.find((s) => s.id === "managed-services");
  if (msSection?.selected) {
    (msSection.options ?? [])
      .filter((s) => s.selected)
      .forEach((s) => payload.push((s as any).rawResource));
  }

  const productSection = sections.find((s) => s.id === "product");
  if (productSection?.selected) {
    (productSection.options ?? [])
      .filter((p) => p.selected)
      .forEach((p) => payload.push((p as any).rawResource));
  }

  // const quoteSection = sections.find((s) => s.id === "vendor-quotes");
  // if (quoteSection?.selected) {
  //   (quoteSection.options ?? [])
  //     .filter((q) => q.selected)
  //     .forEach((q) => payload.push((q as any).rawResource));
  // }

  const governanceSection = sections.find((s) => s.id === "governance");
  if (governanceSection?.selected) {
    (governanceSection.options ?? [])
      .filter((g) => g.selected)
      .forEach((g) => payload.push((g as any).rawResource));
  }

  const resourceSection = sections.find((s) => s.id === "resources");
  if (resourceSection?.selected) {
    (resourceSection.options ?? [])
      .filter((r) => r.selected)
      .forEach((r) => payload.push((r as any).rawResource));
  }

  const expenseSection = sections.find((s) => s.id === "expense");
  if (expenseSection?.selected) {
    (expenseSection.options ?? [])
      .filter((e) => e.selected)
      .forEach((e) => payload.push((e as any).rawResource));
  }

  return payload;
};

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
}

const CreateBlueprintModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { project, isLoading } = useGetProject(projectId);
  const { data: accountData } = useGetAccountQuery({ slug: accountSlug });
  const [createBlueprint, { isLoading: isSubmitting }] =
    useV2CreateBlueprintMutation();

  const [blueprintName, setBlueprintName] = useState<string>("");
  const [sections, setSections] = useState<BlueprintSection[]>(() =>
    buildSections(project || null)
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] =
    useState<"success" | "danger">("success");

  useEffect(() => {
    if (show && !isLoading && project) setSections(buildSections(project));
  }, [show, isLoading]);

  const accountId =
    (accountData?.data?.attributes as any)?.["account-id"] ??
    accountData?.data?.id;

  const handleSubmit = () => {
    if (!blueprintName.trim()) {
      setAlertVariant("danger");
      setAlertMessage("Blueprint name is required.");
      return;
    }

    const payload = buildPayload(sections, project);

    createBlueprint({
      body: {
        data: {
          type: "blueprints",
          attributes: {
            name: blueprintName.trim(),
            payload,
          },
          relationships: {
            account: {
              data: { id: Number(accountId), type: "accounts" },
            },
          },
        },
      },
    })
      .unwrap()
      .then(() => {
        setAlertVariant("success");
        setAlertMessage("Blueprint created successfully.");
        setTimeout(() => {
          setAlertMessage(null);
          onClose();
        }, 1500);
      })
      .catch((err) => {
        const detail = err?.data?.errors?.[0]?.detail;
        setAlertVariant("danger");
        setAlertMessage(
          detail ?? "Something went wrong creating the blueprint."
        );
      });
  };

  return (
    <>
      {alertMessage && (
        <ToastAlert
          variant={alertVariant}
          text={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <ScopeStackModal
        className="createBlueprintFromProjectModal"
        show={show}
        modalTitle="Create Blueprint from Project"
        modalBody={
          isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              <div className="fields">
                <TextField
                  id="name"
                  label="Blueprint Name"
                  placeholder="Enter Blueprint Name"
                  onChange={(e) => setBlueprintName(e.target.value)}
                />
              </div>
              <div className="context">Customize Blueprint</div>
              <div className="description">
                Select the items you wish to include on this blueprint.
              </div>
              <NestedItemSelect
                sections={sections}
                onSectionsChange={setSections}
              />
            </>
          )
        }
        button1Text="Cancel"
        button2Text={isSubmitting ? "Creating..." : "Create Blueprint"}
        handleClose={onClose}
        handleButton1Click={onClose}
        handleButton2Click={handleSubmit}
        button2Disabled={isSubmitting}
      />
    </>
  );
};

export default CreateBlueprintModal;
